import { cn } from '../../lib/cn';
import { formatAmount, formatExact } from '../../lib/format';
import { RESOURCE_ORDER, RESOURCES, type ResourceAmounts, type ResourceKey } from '../../lib/game';
import { ResourceIcon } from './ResourceIcon';
import { Tooltip } from '../primitives/Tooltip';

export interface CostListProps {
  /** What the action costs. */
  cost: ResourceAmounts;
  /** What the player currently holds. Omit to skip affordability checks. */
  available?: ResourceAmounts;
  /** `row` for card footers, `grid` for detail panes. */
  layout?: 'row' | 'grid';
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Resource cost breakdown with affordability marking.
 *
 * Any resource the player can't cover is rendered in danger red — the single
 * most consulted piece of information before committing to a build, so it has
 * to be readable at a glance rather than requiring arithmetic.
 */
export function CostList({
  cost,
  available,
  layout = 'row',
  size = 'sm',
  className,
}: CostListProps) {
  const entries = RESOURCE_ORDER.filter(
    (key): key is ResourceKey => (cost[key] ?? 0) > 0,
  ).map((key) => {
    const required = cost[key] ?? 0;
    const held = available?.[key];
    const short = held !== undefined && held < required;
    return { key, required, held, short };
  });

  if (entries.length === 0) return null;

  return (
    <ul
      className={cn(
        layout === 'row' ? 'flex flex-wrap items-center gap-x-3 gap-y-1' : 'grid grid-cols-2 gap-2',
        className,
      )}
    >
      {entries.map(({ key, required, held, short }) => (
        <li key={key}>
          <Tooltip
            side="top"
            content={
              <div className="space-y-0.5">
                <div className="font-display text-label uppercase text-content">
                  {RESOURCES[key].label}
                </div>
                <div data-sg-numeric className="text-content-secondary">
                  Kosten: {formatExact(required)}
                </div>
                {held !== undefined ? (
                  <div data-sg-numeric className={short ? 'text-danger' : 'text-success'}>
                    Vorrat: {formatExact(held)}
                    {short ? ` (fehlt ${formatExact(required - held)})` : ''}
                  </div>
                ) : null}
              </div>
            }
          >
            <span className="inline-flex items-center gap-1">
              <ResourceIcon resource={key} size={size === 'sm' ? 16 : 18} />
              <span
                data-sg-numeric
                className={cn(
                  'font-medium tabular-nums',
                  size === 'sm' ? 'text-xs' : 'text-sm',
                  short ? 'text-danger-bright' : 'text-content-secondary',
                )}
              >
                {formatAmount(required)}
              </span>
            </span>
          </Tooltip>
        </li>
      ))}
    </ul>
  );
}

/** True when every resource in `cost` is covered by `available`. */
export function canAfford(cost: ResourceAmounts, available: ResourceAmounts): boolean {
  return RESOURCE_ORDER.every((key) => (cost[key] ?? 0) <= (available[key] ?? 0));
}
